// Zebra app-shell — light/dark theme context.
//
// The user's preference is persisted per-account in localStorage so a
// user keeps the same theme on the same device regardless of which
// account they sign in with. We don't fall back to the system colour
// scheme — letting the OS swing the app from light to dark mid-session
// surprises people, and the explicit toggle is what users wanted.
//
// The active theme is applied as a `data-theme="light|dark"` attribute
// on <html>, which the global CSS (web/src/index.css) reads to switch
// the colour token set. Components don't need to consume this context
// unless they want the toggle button itself.

import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { STORAGE_PREFIX } from '@client/config/client.config';

const ThemeContext = createContext(null);

const VALID_THEMES = new Set(['light', 'dark']);
const DEFAULT_THEME = 'light';

function userKey(accountId) {
  // Per-account so each Atlassian login on this device gets its own
  // preference. Falls back to a shared key for the brief window before
  // the user object is loaded.
  return accountId ? `${STORAGE_PREFIX}_theme_${accountId}` : `${STORAGE_PREFIX}_theme`;
}

function readStored(key) {
  try {
    const v = localStorage.getItem(key);
    return VALID_THEMES.has(v) ? v : null;
  } catch { return null; }
}

function applyTheme(theme) {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  // Hint to the browser so native form controls (scrollbars, default
  // checkboxes, file inputs, datetime pickers) switch palettes too.
  root.style.colorScheme = theme;
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const accountId = user?.accountId || null;

  // Initialise from whatever's already in localStorage. We try the
  // account-scoped key first, then the shared fallback. This avoids a
  // light → dark flash for returning users.
  const [theme, setTheme] = useState(() => {
    return readStored(userKey(accountId))
        || readStored(userKey(null))
        || DEFAULT_THEME;
  });

  // When the signed-in user changes, re-resolve the preference from the
  // new account's key so account-switching surfaces their own theme.
  useEffect(() => {
    const next = readStored(userKey(accountId)) || readStored(userKey(null)) || DEFAULT_THEME;
    setTheme(next);
  }, [accountId]);

  // Apply + persist on every change.
  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem(userKey(accountId), theme);
      // Mirror to the shared key so a fresh tab before user-resolution
      // still picks up the right palette.
      localStorage.setItem(userKey(null), theme);
    } catch { /* private browsing / quota */ }
  }, [theme, accountId]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const setExplicit = useCallback((next) => {
    if (VALID_THEMES.has(next)) setTheme(next);
  }, []);

  const value = useMemo(() => ({
    theme,
    isDark: theme === 'dark',
    toggleTheme,
    setTheme: setExplicit,
  }), [theme, toggleTheme, setExplicit]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
